import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Shield, Network, Globe, Cpu, Leaf, CheckCircle2, Info, ArrowRight, Save, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import KineticHero from '@/components/KineticHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOCUS_AREAS = [
  { key: 'economy', icon: DollarSign, title: 'Economy', desc: 'Trade flows, monetary policy, and resource allocation models.', color: '#004ac6' },
  { key: 'security', icon: Shield, title: 'Security', desc: 'Cyber defense, kinetic intelligence, and regional stability metrics.', color: '#004ac6' },
  { key: 'infrastructure', icon: Network, title: 'Infrastructure', desc: 'Energy grids, transportation logistics, and digital connectivity.', color: '#004ac6' },
  { key: 'diplomacy', icon: Globe, title: 'Diplomacy', desc: 'Geopolitical alliances, treaty frameworks, and soft power assets.', color: '#004ac6' },
  { key: 'technology', icon: Cpu, title: 'Technology', desc: 'Emerging AI vectors, semiconductor chains, and R&D velocity.', color: '#004ac6' },
  { key: 'climate', icon: Leaf, title: 'Climate', desc: 'Environmental resilience, resource scarcity, and carbon transition.', color: '#004ac6' },
];

const SYSTEM_IMG = 'https://lh3.googleusercontent.com/aida-public/AB6AXuBowhQUIKm_TCYSF4WmjHUm6nEc7zilCDtA4hIAFFH_7swUyF00GN7VZ-6rQt4N9psUU-YEdxLopU1OmRvoSii5qHTCfr43xZH-HABvhm_HoONKG52WuOFdFf3SzdRghGB48xz9AFTdMFZMrf6-D_xdTOUo0BYKXIBd3kcbISq28z-OIWCNXFL30i7JGzvbUvt8asnj0vR8o9UY7QwL1Iep-FB30ri2UA9VKNVSTnygdnCGvhpZ8QvUXc1YbyX0fEhwbXEzwWgaDjmd';

function BlueprintConfigPage() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [projectCode, setProjectCode] = useState('');
  const [step, setStep] = useState(1);

  useEffect(() => {
    
    axios.get(`${API}/countries`, {  })
      .then(res => setCountries(res.data))
      .catch(() => toast.error('Failed to load'));
  }, []);

  const toggleArea = (key) => {
    setSelectedAreas(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleContinue = () => {
    if (!selectedCountry) { toast.error('Select a country first'); return; }
    navigate(`/platform/generator?country=${selectedCountry}&areas=${selectedAreas.join(',')}`);
  };

  return (
    <div data-testid="blueprint-config-page" className="min-h-screen bg-[#f8f9ff] text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="bpcfg"
        eyebrow="3-step blueprint wizard"
        title="Start"
        accent="Blueprint"
        subtitle="Pick your country target and the focus areas that matter to your IC. Our AI intelligence engine handles the rest."
      />
      <div className="p-6 lg:p-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-xs text-[#434655] cursor-pointer hover:text-[#004ac6]" onClick={() => navigate('/platform')}>Dashboard</span>
        <ChevronRight className="w-3 h-3 text-[#c3c6d7]" />
        <span className="text-xs text-[#004ac6] font-semibold">Configuration</span>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-3 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            s === step ? 'bg-[#004ac6] text-white' : s < step ? 'bg-green-500 text-white' : 'bg-[#e5eeff] text-[#434655]'
          }`}>
            {s < step ? <CheckCircle2 className="w-4 h-4" /> : s}
          </div>
        ))}
      </div>

      {/* Country Selection */}
      <div className="mb-8">
        <label className="text-xs font-bold uppercase tracking-widest text-[#434655] mb-2 block">Target Country</label>
        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
          <SelectTrigger data-testid="config-country-select" className="w-full max-w-md bg-white">
            <SelectValue placeholder="Select country to analyze" />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {countries.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name} — {c.sector?.slice(0, 30) || 'General'}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Focus Areas Grid */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FOCUS_AREAS.map((area) => {
              const Icon = area.icon;
              const selected = selectedAreas.includes(area.key);
              return (
                <button key={area.key} onClick={() => toggleArea(area.key)}
                  data-testid={`area-${area.key}`}
                  className={`p-5 rounded-xl text-left transition-all border ${
                    selected
                      ? 'bg-[#004ac6] text-white border-[#004ac6] shadow-lg shadow-[#004ac6]/20'
                      : 'bg-white text-[#0b1c30] border-[#c3c6d7]/30 hover:border-[#004ac6]/30 hover:shadow-sm'
                  }`}>
                  <Icon className={`w-6 h-6 mb-3 ${selected ? 'text-white' : 'text-[#004ac6]'}`} />
                  <h3 className="font-bold text-sm tracking-tight mb-1">{area.title}</h3>
                  <p className={`text-xs leading-relaxed ${selected ? 'text-white/80' : 'text-[#434655]'}`}>{area.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Blueprint Summary Panel */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-bold text-[#0b1c30] mb-4">Blueprint Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-xs">
                <span className="text-[#434655]">Active Vectors</span>
                <span className="font-bold text-[#0b1c30]">{selectedAreas.length} Selected</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[#434655]">Depth Level</span>
                <span className="font-bold text-[#0b1c30]">Strategic Analysis</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[#434655]">Processing Speed</span>
                <span className="font-bold text-[#0b1c30]">Standard</span>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs text-[#434655] block mb-1.5">Project Codename</label>
              <Input value={projectCode} onChange={e => setProjectCode(e.target.value)}
                placeholder="e.g., NOR-STR-04" className="text-sm" data-testid="project-code" />
            </div>
          </div>

          <Button onClick={handleContinue} className="w-full bg-[#004ac6] text-white gap-2 font-bold" data-testid="continue-btn">
            Continue to Parameters <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" className="w-full gap-2 text-xs text-[#434655]">
            <Save className="w-3.5 h-3.5" /> Save Draft
          </Button>

          {/* Pro Tip */}
          <div className="bg-[#eff4ff] border border-[#004ac6]/10 p-4 rounded-xl">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-[#004ac6] mt-0.5 shrink-0" />
              <div>
                <h4 className="text-xs font-bold text-[#0b1c30] mb-0.5">Pro Tip</h4>
                <p className="text-[11px] text-[#434655] leading-relaxed">Combining Economy and Infrastructure focus areas provides high-resolution data for long-term regional development reports.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Capabilities */}
      <section className="mt-12 mb-8">
        <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight mb-5">System Capabilities</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <img src={SYSTEM_IMG} alt="System" className="w-full h-48 object-cover" />
            <div className="p-5">
              <h3 className="font-bold text-sm text-[#0b1c30] mb-1">Multi-Scalar Intelligence</h3>
              <p className="text-xs text-[#434655] leading-relaxed">Our neural architecture processes global trends down to local micro-variables with 99.4% accuracy.</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col justify-center">
            <div className="w-10 h-10 rounded-lg bg-[#e5eeff] flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5 text-[#004ac6]" />
            </div>
            <h3 className="font-bold text-sm text-[#0b1c30] mb-1">Real-time Synthesis</h3>
            <p className="text-xs text-[#434655] leading-relaxed">Reports are generated and updated as intelligence streams are received by the unit.</p>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}

export default BlueprintConfigPage;
